import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Slider, Select, Radio, Input, Form, Modal, message } from 'antd';
import validator from 'validator';
import throttle from 'lodash.throttle';
import sanitizeHtml from 'sanitize-html';
import { isLight } from '../../../modules/colorHelper';
import { extraColors } from '../../../constants/extraColors';
import { EditorElementPopover } from '../EditorElementPopover';
import {
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  DeleteOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const FormItem = Form.Item;

export class PButton extends React.Component {
  state = {
    showBtn: false,

    targetValue: this.props.target,
    targetValidateStatus: 'success',
    targetHelp: null,

    textValidateStatus: 'success',
    textHelp: '',

    nameValidateStatus: 'success',
    nameHelp: '',
  };

  static propTypes = {
    // text on the button (default: Click)
    text: PropTypes.string,
    // sm | md (default) | lg | xl | xxl
    size: PropTypes.string,
    // primary (default) | accent1 | accent 2 | accent3 | bgLight | bgDark | bgLightAlt | bgDarkAlt
    // black-hi, black-md, black-lo, white-hi, white-md, white-lo
    color: PropTypes.string,
    // solid | outline | link
    type: PropTypes.string,

    // url (default) | email | phone
    targetType: PropTypes.string,
    target: PropTypes.string,

    // submit button belongs to a form
    isSubmit: PropTypes.bool,

    // relevant if isSubmit === false
    name: PropTypes.string,

    className: PropTypes.string,
    style: PropTypes.object,

    isDeletable: PropTypes.bool,

    colorScheme: PropTypes.object,
    fontScheme: PropTypes.object,
    address: PropTypes.array,
    updateNode: PropTypes.func,
    deleteElement: PropTypes.func,
    publish: PropTypes.bool,
  };

  static defaultProps = {
    color: 'primary',
    text: 'Click',
    type: 'solid',
    targetType: 'url',
    target: '',
    name: 'CTA Button',
    publish: false,
    isDeletable: true,
    isSubmit: false,
  };

  marks = {
    0: 'xs',
    10: 'sm',
    20: 'md',
    30: 'lg',
    40: 'xl',
    50: 'xxl',
  };

  getCurrentMarks = (size) => {
    let result = null;
    Object.keys(this.marks).forEach(key => {
      if (this.marks[key] === size) {
        result = key;
      }
    });
    return result ? parseInt(result, 10) : 20;
  }

  validateUrl = (url) => /^((https?):\/\/)?([a-z0-9]+\.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/i.test(url);

  validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email.trim()).toLowerCase());
  };

  handleIconSizeChange = (value) => {
    const nextSize = this.marks[value];
    this.props.updateNode(this.props.address, { size: nextSize });
  }

  iconColorSelected = (color) => {
    this.props.updateNode(this.props.address, { color });
  }

  makePill = (c) => {
    let color = null;
    if (c in this.props.colorScheme) {
      color = this.props.colorScheme[c];
    }
    else {
      color = extraColors[c];
    }

    return (
      <span
        onClick={() => this.iconColorSelected(c)}
        className={cx(
          'bgpill',
          { selected: (c === this.props.color) }
        )}
        style={{
          backgroundColor: color,
        }}
      />
    );
  };

  validateTargetAsPhone = () => {
    this.setState({
      targetValidateStatus: 'success',
      targetHelp: null,
    });
    return true;
  }

  validateTargetAsEmail = (email) => {
    const validated = validator.isEmail(email);
    if (!validated) {
      this.setState({
        targetValidateStatus: 'warning',
        targetHelp: 'Enter a valid email',
      });
      return false;
    }
    this.setState({
      targetValidateStatus: 'success',
      targetHelp: null,
    });
    return true;
  }

  validateTargetAsUrl = (url) => {
    if (!validator.isURL(url)) {
      this.setState({
        targetValidateStatus: 'warning',
        targetHelp: 'Enter a valid url',
      });
      return false;
    }
    this.setState({
      targetValidateStatus: 'success',
      targetHelp: null,
    });
    return true;
  }

  targetChanged = (e) => {
    const { value } = e.target;
    const target = value.trim();
    const { targetType } = this.props;

    let validated = false;
    if (targetType === 'url') {
      validated = this.validateTargetAsUrl(target);
    }
    else if (targetType === 'email') {
      validated = this.validateTargetAsEmail(target);
    }
    else if (targetType === 'phone') {
      validated = this.validateTargetAsPhone(target);
    }
    if (validated) {
      this.props.updateNode(this.props.address, { target });
    }

    this.setState({ targetValue: target });
  }

  onTextChange = throttle((value) => {
    const sanitized = sanitizeHtml(value.trim(), {
      allowedTags: [],
      allowedAttributes: {},
    });
    if (sanitized.length === 0) {
      this.setState({
        textValidateStatus: 'warning',
        textHelp: 'Cannot be empty',
      });
      this.props.updateNode(this.props.address, { text: 'Click' });
    }
    else if (sanitized.length > 32) {
      this.setState({
        textValidateStatus: 'warning',
        textHelp: 'Too long',
      });
    }
    else {
      this.setState({
        textValidateStatus: 'success',
        textHelp: null,
      });
      this.props.updateNode(this.props.address, { text: sanitized });
    }
  }, 500);

  targetTypeChanged = (activeKey) => {
    if (activeKey === this.props.targetType) {
      return;
    }
    this.props.updateNode(this.props.address, {
      targetType: activeKey,
      target: '',
    });
    this.setState({ targetValue: null });
  };

  handleButtonSizeChange = (value) => {
    const nextSize = this.marks[value];
    this.props.updateNode(this.props.address, { size: nextSize });
  };

  onButtonTypeChange = (e) => {
    const type = e.target.value;
    this.props.updateNode(this.props.address, { type });
  }

  onNameChange = throttle((value) => {
    const sanitized = sanitizeHtml(value.trim(), {
      allowedTags: [],
      allowedAttributes: {},
    });
    if (sanitized.length === 0) {
      this.setState({
        nameValidateStatus: 'warning',
        nameHelp: 'Cannot be empty',
      });
    }
    else if (sanitized.length > 16) {
      this.setState({
        nameValidateStatus: 'warning',
        nameHelp: 'Too long',
      });
    }
    else {
      this.setState({
        nameValidateStatus: 'success',
        nameHelp: null,
      });
      this.props.updateNode(this.props.address, { name: sanitized });
    }
  }, 300);

  onPopoverMouseOver = (e) => {
    e.stopPropagation();
  };

  onPopoverMouseOut = (e) => {
    e.stopPropagation();
  };

  confirmButtonRemoval = () => {
    Modal.confirm({
      title: 'Are you sure?',
      content: null,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Never mind',
      onOk: () => {
        this.props.deleteElement(this.props.address);
      },
      onCancel() {},
    });
  }

  popoverContent = () => {
    const {
      type,
      size,
      name,
      targetType,
      text,
      isSubmit,
      isDeletable,
    } = this.props;

    const targetSelectOptions = (
      <Select defaultValue={targetType} onChange={this.targetTypeChanged}>
        <Select.Option value="url"><LinkOutlined /></Select.Option>
        <Select.Option value="email"><MailOutlined /></Select.Option>
        <Select.Option value="phone"><PhoneOutlined /></Select.Option>
      </Select>
    );

    let targetPlaceholder = 'Enter a URL';
    if (targetType === 'email') {
      targetPlaceholder = 'Enter an email';
    }
    if (targetType === 'phone') {
      targetPlaceholder = 'Enter a phone';
    }

    const targetNode = (
      <React.Fragment>
        <div className="label">
          <div className="main">Link</div>
        </div>
        <FormItem
          validateStatus={this.state.targetValidateStatus}
          hasFeedback={false}
          help={this.state.targetHelp}
        >
          <Input
            addonBefore={targetSelectOptions}
            onChange={this.targetChanged}
            value={this.state.targetValue}
            placeholder={targetPlaceholder}
          />
        </FormItem>
        <div className="divider" />
      </React.Fragment>
    );

    return (
      <React.Fragment>
        <div className="label">
          <div className="main">Label</div>
        </div>

        <FormItem
          validateStatus={this.state.textValidateStatus}
          hasFeedback={false}
          help={this.state.textHelp}
        >
          <Input onChange={(e) => { this.onTextChange(e.target.value); }} defaultValue={text} />
        </FormItem>

        <div className="divider" />

        { isSubmit ? null : targetNode }

        <div className="label">
          <div className="main">Color</div>
        </div>
        <div className="bgpillContainer mb-2 text-center">
          {this.makePill('primary')}
          {this.makePill('accent1')}
          {this.makePill('accent2')}
          {this.makePill('accent3')}
          {this.makePill('bgLight')}
          {this.makePill('bgLightAlt')}
          {this.makePill('bgDark')}
          {this.makePill('bgDarkAlt')}
        </div>
        <div className="bgpillContainer text-center">
          {this.makePill('black-hi')}
          {this.makePill('black-md')}
          {this.makePill('black-lo')}
          {this.makePill('white-hi')}
          {this.makePill('white-md')}
          {this.makePill('white-lo')}
        </div>

        <div className="divider" />

        <div className="label">
          <div className="main">Style</div>
        </div>
        <Radio.Group defaultValue={type} buttonStyle="solid" size="small" onChange={this.onButtonTypeChange}>
          <Radio.Button value="solid">Solid</Radio.Button>
          <Radio.Button value="outline">Outline</Radio.Button>
          <Radio.Button value="link">Link</Radio.Button>
        </Radio.Group>

        <div className="divider" />

        <div className="label">
          <div className="main">Size</div>
        </div>

        <div>
          <Slider
            marks={this.marks}
            step={null}
            min={0} max={50}
            defaultValue={this.getCurrentMarks(size)}
            tipFormatter={null}
            onAfterChange={this.handleButtonSizeChange}
          />
        </div>

        <div className="divider" />

        {(isSubmit === true) ? null : (
          <React.Fragment>

            <div className="label">
              <div className="main">Name</div>
              <div className="help">Used only for reporting. Hidden from visitors.</div>
            </div>

            <FormItem validateStatus={this.state.nameValidateStatus} hasFeedback={false} help={this.state.nameHelp} >
              <Input onChange={(e) => { this.onNameChange(e.target.value); }} defaultValue={name} />
            </FormItem>

            <div className="divider" />

          </React.Fragment>
        )}

        {
          isDeletable ? (
            <React.Fragment>
              <Button
                type="danger" block icon={<DeleteOutlined />} className="mt-3"
                onClick={this.confirmButtonRemoval}
              >
                Remove this button
              </Button>
              <div className="divider" />
            </React.Fragment>
          ) : null
        }

      </React.Fragment>
    );
  }

  isSizeSelected = (csize) => (
    (this.props.size === csize) ? <CheckOutlined /> : null
  );

  onMouseOver = (e) => {
    this.setState({ showBtn: true });
    e.stopPropagation();
  };

  onMouseOut = (e) => {
    this.setState({ showBtn: false });
    e.stopPropagation();
  };

  getSolidColor = (c) => {
    const { colorScheme } = this.props;
    const mycolor = (typeof c === 'undefined') ? 'primary' : c;
    if (mycolor in colorScheme) {
      return colorScheme[mycolor];
    }
    return extraColors[c];
  };

  linkClicked = (e) => {
    e.preventDefault();
    message.warning('Links disabled in edit mode');
  }

  render() {
    const {
      color, type, size,
      target, targetType,
      text,
      publish, fontScheme, isSubmit } = this.props;
    const solidColor = this.getSolidColor(color);
    const buttonType = isSubmit ? 'submit' : 'button';

    let fontFamily = null;
    let fontWeight = null;
    if (size.startsWith('header-')) {
      [fontFamily] = fontScheme.family;
      [fontWeight] = fontScheme.weight;
    }
    else if (size.startsWith('subheader-')) {
      [, fontFamily] = fontScheme.family;
      [, fontWeight] = fontScheme.weight;
    }
    else {
      [, , fontFamily] = fontScheme.family;
      [, , fontWeight] = fontScheme.weight;
    }

    let backgroundColorStyle = null;
    let borderColorStyle = null;
    let textColorStyle = null;
    if (type === 'solid') {
      backgroundColorStyle = { backgroundColor: solidColor };
      borderColorStyle = { borderColor: 'transparent' };
      textColorStyle = { color: isLight(solidColor) ? '#000000cc' : 'white' };
    }
    else if (type === 'outline') {
      backgroundColorStyle = { backgroundColor: 'transparent' };
      borderColorStyle = { borderColor: solidColor };
      textColorStyle = { color: solidColor };
    }
    else if (type === 'link') {
      backgroundColorStyle = { backgroundColor: 'transparent' };
      borderColorStyle = { borderColor: 'transparent' };
      textColorStyle = { color: solidColor };
    }

    const buttonNodeInner = (
      <button
        type={buttonType}
        className={cx(
          'pbutton',
          `pbutton-type-${type}`,
          `pbutton-size-${size}`,
        )}
        style={{
          ...this.props.style,
          ...backgroundColorStyle,
          ...borderColorStyle,
          ...textColorStyle,
          fontFamily: `${fontFamily}`,
          fontWeight: `${fontWeight}`,
        }}
      >
        {text}
      </button>
    );

    let hrefVal = target;
    if (targetType === 'email') {
      hrefVal = `mailto:${hrefVal}`;
    }
    else if (targetType === 'phone') {
      hrefVal = `tel:${hrefVal}`;
    }

    let buttonNode = buttonNodeInner;
    if (!isSubmit) {
      buttonNode = (
        <a href={hrefVal} onClick={this.linkClicked}>
          { buttonNodeInner }
        </a>
      );
    }

    return (
      <div
        className={cx(
          'pbutton',
          this.props.className,
        )}
        onMouseOver={this.onMouseOver}
        onFocus={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onBlur={this.onMouseOut}
      >

        { buttonNode }

        <EditorElementPopover
          usecase="button"
          showBtn={this.state.showBtn}
          btnLabel="button"
          title="Button appearance"
          content={this.popoverContent()}
          placement="bottomLeft"
          publish={publish}
        />

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/editor/elements/PButton.jsx